import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wand2, Download, Upload, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { resolveMedia } from "../../lib/media";
import { generateShareCard } from "../../lib/shareCard";

function Option({ label, url, chosen, onPick, testId }) {
  return (
    <div className={`border-2 p-2 ${chosen ? "border-red-600" : "border-zinc-200"}`} data-testid={testId}>
      <div className="text-[11px] font-bold uppercase tracking-widest mb-1">{label}</div>
      {url ? <img src={resolveMedia(url)} alt={label} className="w-full aspect-square object-cover bg-zinc-100" /> : <div className="aspect-square bg-zinc-100 flex items-center justify-center text-xs text-zinc-500">Not created yet</div>}
      {url && (
        <div className="flex gap-2 mt-2">
          <button onClick={onPick} className={`text-xs px-2 py-1 border ${chosen ? "bg-red-600 text-white border-red-600" : "border-zinc-900"}`} data-testid={`${testId}-pick`}>{chosen ? "Selected" : "Use this"}</button>
          <a href={resolveMedia(url)} download target="_blank" rel="noreferrer" className="text-xs px-2 py-1 border border-zinc-900 inline-flex items-center gap-1" data-testid={`${testId}-download`}><Download size={11} /> Download</a>
        </div>
      )}
    </div>
  );
}

export default function AdminShareImagePanel({ rescue, headers, onChanged }) {
  const [choice, setChoice] = useState(rescue.share_image_choice || "generated");
  const [approved, setApproved] = useState(rescue.share_image_approved);
  const [busy, setBusy] = useState("");
  useEffect(() => { setChoice(rescue.share_image_choice || "generated"); setApproved(rescue.share_image_approved); }, [rescue]);

  const generate = async () => {
    setBusy("gen");
    try {
      const blob = await generateShareCard(rescue);
      const fd = new FormData();
      fd.append("file", blob, "share-card.png");
      await api.post(`/admin/urgent-rescues/${rescue.id}/share-image/upload`, fd, { headers });
      toast.success("Branded 1080×1080 card generated. Review it, then approve.");
      onChanged();
    } catch { toast.error("Could not generate the card."); }
    finally { setBusy(""); }
  };
  const uploadFlyer = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("flyer");
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/admin/urgent-rescues/${rescue.id}/flyer/upload`, fd, { headers }); toast.success("Flyer uploaded."); onChanged(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Flyer upload failed."); }
    finally { setBusy(""); e.target.value = ""; }
  };
  const save = async () => {
    setBusy("save");
    try {
      await api.post(`/admin/urgent-rescues/${rescue.id}/share-image`, { choice, approved }, { headers });
      toast.success(approved ? "Share image approved for the public page." : "Saved — share image stays private until approved.");
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed."); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-3" data-testid="share-image-panel">
      <div className="flex flex-wrap gap-2">
        <button onClick={generate} disabled={!!busy} className="text-xs px-3 py-2 bg-zinc-900 text-white inline-flex items-center gap-1 disabled:opacity-60" data-testid="share-generate-btn">
          {busy === "gen" ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />} {rescue.generated_share_url ? "Regenerate" : "Generate"} branded card
        </button>
        <label className="text-xs px-3 py-2 border border-zinc-900 inline-flex items-center gap-1 cursor-pointer" data-testid="share-flyer-upload-label">
          {busy === "flyer" ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload custom flyer
          <input type="file" accept="image/*" className="hidden" onChange={uploadFlyer} data-testid="share-flyer-upload-input" />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3 max-w-xl">
        <Option label="Generated card" url={rescue.generated_share_url} chosen={choice === "generated"} onPick={() => setChoice("generated")} testId="share-option-generated" />
        <Option label="Custom flyer" url={rescue.flyer_url} chosen={choice === "flyer"} onPick={() => setChoice("flyer")} testId="share-option-flyer" />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!approved} onChange={(e) => setApproved(e.target.checked)} data-testid="share-approve-checkbox" />
        Approve this image for the public rescue page
      </label>
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={!!busy} className="btn-danger disabled:opacity-60" data-testid="share-save-btn">Save image choice</button>
        <span className="text-xs text-zinc-600" data-testid="share-status">
          {rescue.share_image_approved ? <span className="text-green-700 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Public: {rescue.share_image_choice}</span> : "Not approved — hidden from public"}
        </span>
      </div>
    </div>
  );
}
